import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { addDaysIso, formatCurrency, todayIso } from '../../utils/formatters.js';

import { SupplierCreateModal } from './SupplierCreateModal.jsx';

function emptyLine() {
  return { productId: '', quantity: '1', unitCost: '0' };
}
function emptyForm() {
  return {
    supplierId: '',
    warehouseId: '',
    orderDate: todayIso(),
    expectedDate: addDaysIso(14),
    notes: '',
    lines: [emptyLine()],
  };
}

export function PurchaseOrderCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);

  const suppliers = useQuery({
    queryKey: ['inventory', 'suppliers'],
    queryFn: () => inventoryApi.listSuppliers().then((r) => r.items || []),
    enabled: open,
  });
  const warehouses = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => inventoryApi.listWarehouses().then((r) => r.items || []),
    enabled: open,
  });
  const products = useQuery({
    queryKey: ['inventory', 'products'],
    queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items || []),
    enabled: open,
  });

  const lineTotals = form.lines.map((l) => Number(l.quantity || 0) * Number(l.unitCost || 0));
  const grandTotal = lineTotals.reduce((acc, n) => acc + n, 0);

  const create = useMutation({
    mutationFn: (body) => inventoryApi.createPurchaseOrder(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'purchase-orders'] });
      setForm(emptyForm());
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const patchField = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const patchLine = (idx, key, value) =>
    setForm((p) => ({
      ...p,
      lines: p.lines.map((line, i) => (i === idx ? { ...line, [key]: value } : line)),
    }));
  const addLine = () => setForm((p) => ({ ...p, lines: [...p.lines, emptyLine()] }));
  const removeLine = (idx) =>
    setForm((p) => ({
      ...p,
      lines: p.lines.length > 1 ? p.lines.filter((_, i) => i !== idx) : p.lines,
    }));

  function submit(e) {
    e.preventDefault();
    setError(null);
    create.mutate({
      supplierId: form.supplierId,
      warehouseId: form.warehouseId,
      orderDate: form.orderDate,
      expectedDate: form.expectedDate || undefined,
      notes: form.notes || undefined,
      lines: form.lines.map((l) => ({
        productId: l.productId,
        quantity: Number(l.quantity || 0),
        unitCost: Number(l.unitCost || 0),
      })),
    });
  }

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title={t('inv.po.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('inv.po.modal.supplier')}</span>
            <div className="inline-create">
              <select className="select" value={form.supplierId} onChange={(e) => patchField('supplierId', e.target.value)} required>
                <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
                {(suppliers.data || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.code} - {s.name}</option>
                ))}
              </select>
              <Button type="button" variant="ghost" className="inline-create-btn" onClick={() => setSupplierModalOpen(true)}>
                + {t('common.new')}
              </Button>
            </div>
          </label>
          <label className="field">
            <span className="field-label">{t('inv.po.modal.warehouse')}</span>
            <select className="select" value={form.warehouseId} onChange={(e) => patchField('warehouseId', e.target.value)} required>
              <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
              {(warehouses.data || []).map((w) => (
                <option key={w.id} value={w.id}>{w.code} - {w.name}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('inv.po.modal.orderDate')}</span>
            <input className="input" type="date" value={form.orderDate} onChange={(e) => patchField('orderDate', e.target.value)} required />
          </label>
          <label className="field">
            <span className="field-label">{t('inv.po.modal.expectedDate')}</span>
            <input className="input" type="date" value={form.expectedDate} onChange={(e) => patchField('expectedDate', e.target.value)} />
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span className="field-label">{t('inv.po.modal.lines')}</span>
          <Button size="sm" variant="ghost" onClick={addLine}>+ {t('finance.invoices.modal.addLine')}</Button>
        </div>
        {form.lines.map((line, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
              padding: 'var(--space-3)', border: '1px solid var(--color-hairline)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <label className="field">
              <span className="field-label">{t('inv.po.modal.product')}</span>
              <select className="select" value={line.productId} onChange={(e) => patchLine(idx, 'productId', e.target.value)} required>
                <option value="">{t('inv.po.modal.pickProduct')}</option>
                {(products.data || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.sku} - {p.name}</option>
                ))}
              </select>
            </label>
            <div className="field-row">
              <label className="field">
                <span className="field-label">{t('inv.po.modal.quantity')}</span>
                <input
                  className="input"
                  type="number"
                  min="0.0001"
                  step="any"
                  value={line.quantity}
                  onChange={(e) => patchLine(idx, 'quantity', e.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">{t('inv.po.modal.unitCost')}</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.unitCost}
                  onChange={(e) => patchLine(idx, 'unitCost', e.target.value)}
                  required
                />
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="muted text-sm">
                {t('inv.po.modal.lineTotal')}: <strong>{formatCurrency(lineTotals[idx])}</strong>
              </span>
              {form.lines.length > 1 && (
                <Button size="sm" variant="danger" onClick={() => removeLine(idx)}>
                  {t('finance.invoices.modal.removeLine')}
                </Button>
              )}
            </div>
          </div>
        ))}

        <div style={{
          display: 'flex', justifyContent: 'space-between',
          borderTop: '1px solid var(--color-hairline)', paddingTop: 'var(--space-3)',
        }}>
          <span>{t('inv.po.modal.poTotal')}</span>
          <strong className="mono">{formatCurrency(grandTotal)}</strong>
        </div>

        <label className="field">
          <span className="field-label">{t('inv.po.modal.notes')}</span>
          <textarea className="textarea" rows={2} value={form.notes} onChange={(e) => patchField('notes', e.target.value)} />
        </label>

        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
    <SupplierCreateModal
      open={supplierModalOpen}
      onClose={() => setSupplierModalOpen(false)}
      onCreated={(created) => {
        queryClient.invalidateQueries({ queryKey: ['inventory', 'suppliers'] });
        if (created?.id) {
          patchField('supplierId', created.id);
        }
      }}
    />
    </>
  );
}
